import axios, { AxiosResponse } from 'axios';
import { DailyMachine, DailyRental, DailySubContractor, DailyTempWorker, DailyTool, Site } from '../planning';
import { apiClient } from '../endpoints';
import { DataItem } from '../activities';
import { DailySupply } from './dailySupplies';
import { DailyTruckRide } from './dailyTrucksRides';
import { DailyTruck } from './dailyTrucks';
import { User } from '../users';
import { DailyTruckRenter } from './dailyTruckRenters';
import { DailyWaste } from './dailyWastes';
import { Machine } from '../materials/machines';
import { Tool } from '../materials/tools';
import { Truck } from '../materials/trucks';

export interface ValidationSiteSummary {
    site: Site;
    nbActivities: number;
    nbValidatedActivities: number;
    nbInValidationActivities: number;
    nbMissingActivities: number;
    siteState: number;
    totalDailyPrice: number;
}

export interface ValidationSiteSummaryResponse {
    message: string;
    data: ValidationSiteSummary[];
}

export interface ValidationSiteSummaryQueries {
    date: string;
}

// ---- GET Validation Sites ----
export const getValidationSitesSummary = async (queries?: ValidationSiteSummaryQueries): Promise<ValidationSiteSummaryResponse> => {
    try {
        const response: AxiosResponse<ValidationSiteSummaryResponse> = await apiClient.get(`/activities/validation/sites`, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// ---- Types ----

export interface DailyTruckRideSiteReport extends DailyTruckRide {
    site?: Site;
    user?: User;
}
export interface SiteReport {
    site: Site;
    activities: DataItem[];
    dailyMachines: DailyMachine[];
    dailyTools: DailyTool[];
    dailyRentals: DailyRental[];
    dailySupplies: DailySupply[];
    dailyTempWorkers: DailyTempWorker[];
    dailySubContractors: DailySubContractor[];
    dailyTrucks: DailyTruck[];
    dailyTruckRenters: DailyTruckRenter[];
    dailyManagerTruckRides: DailyTruckRideSiteReport[];
    dailyDriversTruckRides: DailyTruckRideSiteReport[];
    dailyExternalManagersTruckRides: DailyTruckRideSiteReport[];
    dailyExternalDriversTruckRides: DailyTruckRideSiteReport[];
    dailyWastes: DailyWaste[];
}

export interface SiteReportResponse {
    message: string;
    data: SiteReport;
}

export interface SiteReportQueries {
    date: string;
}

// ---- GET Site Report ----
export const getSiteReport = async (siteId: string, stepMode: number, queries: SiteReportQueries): Promise<SiteReportResponse> => {
    try {
        const response: AxiosResponse<SiteReportResponse> = await apiClient.get(`/activities/validation/site/${siteId}/stepMode/${stepMode}/report`, {
            params: queries,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// ---- Types ----
export interface MissingReportsResponse {
    message: string;
    data: User[];
}

export interface ValidateSiteActivitiesResponse {
    message: string;
    data: any;
}

export interface CloseValidationForDayResponse {
    message: string;
    data: { validatedReportsCount: number };
}

// ---- GET Users Missing Reports ----
export const getUsersMissingReports = async (siteId: string, queries: { date: string }): Promise<MissingReportsResponse> => {
    try {
        const response: AxiosResponse<MissingReportsResponse> = await apiClient.get(`/activities/validation/site/${siteId}/missing-reports`, {
            params: queries,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export interface ValidateActivitiesBody {
    date: string;
    activityState: number;
}

// ---- PATCH Validate Site Activities ----
export const validateSiteActivities = async (siteId: string, body: ValidateActivitiesBody): Promise<ValidateSiteActivitiesResponse> => {
    try {
        const response: AxiosResponse<ValidateSiteActivitiesResponse> = await apiClient.patch(`/activities/validation/site/${siteId}/validate`, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// ---- PATCH Close Validation For Day ----
export const closeValidationForDay = async (body: { date: string }): Promise<CloseValidationForDayResponse> => {
    try {
        const response: AxiosResponse<CloseValidationForDayResponse> = await apiClient.patch(`/activities/validation/close-day`, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// ---- PATCH Transfer Daily Elements ----
export interface TransferDailyElementsBody {
    sourceActivityId: string;
    targetActivityId: string;
}

export interface TransferDailyElementsResponse {
    message: string;
    data: {
        transferredElementsCount: number;
    };
}

export const transferDailyElements = async (body: TransferDailyElementsBody): Promise<TransferDailyElementsResponse> => {
    try {
        const response: AxiosResponse<TransferDailyElementsResponse> = await apiClient.patch(`/activities/validation/transfer-daily`, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// ---- PATCH Set Pending Infos For Site Activities ----
export interface SetPendingInfosForSiteBody {
    date: string;
    hasPendingInfos: boolean;
}

export interface SetPendingInfosForSiteResponse {
    message: string;
    data: {
        matchedCount: number;
        modifiedCount: number;
    };
}

export const setPendingInfosForSiteActivities = async (siteId: string, body: SetPendingInfosForSiteBody): Promise<SetPendingInfosForSiteResponse> => {
    try {
        const response: AxiosResponse<SetPendingInfosForSiteResponse> = await apiClient.patch(
            `/activities/validation/site/${siteId}/set-pending-infos`,
            body
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export interface DailyItemSiteQuantity {
    site: Site;
    quantity: number;
}

export interface DailyUserItem {
    user: User;
    sites: DailyItemSiteQuantity[];
}

export interface DailyMachineItem {
    machine: Machine;
    sites: DailyItemSiteQuantity[];
}

export interface DailyToolItem {
    tool: Tool;
    sites: DailyItemSiteQuantity[];
}

export interface DailyTruckItem {
    priceType: number;
    truck: Truck;
    sites: DailyItemSiteQuantity[];
}

export interface DailyItemsByDate {
    date: string;
    users: DailyUserItem[];
    machines: DailyMachineItem[];
    tools: DailyToolItem[];
    trucks: DailyTruckItem[];
}

export interface DailyItemsByDateResponse {
    message: string;
    data: DailyItemsByDate;
}

export const getDailyItemsWithQuantityByDate = async (date: string): Promise<DailyItemsByDateResponse> => {
    try {
        const response: AxiosResponse<DailyItemsByDateResponse> = await apiClient.get(`/activities/validation/daily-items/${date}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};
